// Auto-save coordination for PatientWorkspaceTabs in mode="session".
// Each tab wraps its mutation promises via register(promise); handleComplete
// calls waitForIdle() before flipping the examination to completed so no
// in-flight save is lost.
export function createAutoSaveRegistry() {
  const listeners = new Set();
  const flushers = new Set();
  let pending = 0;
  let failed = 0;

  const emit = () => {
    listeners.forEach((fn) => fn({ pending, failed }));
  };

  const register = (promise) => {
    pending += 1;
    emit();
    return promise
      .then((res) => {
        pending -= 1;
        emit();
        return res;
      })
      .catch((err) => {
        pending -= 1;
        failed += 1;
        emit();
        throw err;
      });
  };

  const resetFailure = () => {
    failed = 0;
    emit();
  };

  const waitForIdle = (timeoutMs = 3000) =>
    new Promise((resolve) => {
      const start = Date.now();
      const check = () => {
        if (pending === 0) return resolve({ timedOut: false });
        if (Date.now() - start >= timeoutMs) return resolve({ timedOut: true });
        setTimeout(check, 100);
      };
      check();
    });

  const subscribe = (fn) => {
    listeners.add(fn);
    fn({ pending, failed });
    return () => listeners.delete(fn);
  };

  // Tabs that own debounced auto-saves register a flush callback here so
  // Muayene Tamamla can force pending dirty state to commit immediately
  // instead of relying on the debounce timer to fire.
  const registerFlush = (fn) => {
    flushers.add(fn);
    return () => flushers.delete(fn);
  };

  const flushAll = async () => {
    const out = [];
    flushers.forEach((fn) => {
      try {
        const r = fn();
        if (r && typeof r.then === "function") out.push(r);
      } catch (_) {
        // Ignore — individual tab's flush errors surface via register/promise.
      }
    });
    await Promise.all(out);
  };

  return {
    register,
    registerFlush,
    flushAll,
    waitForIdle,
    resetFailure,
    subscribe,
    get pendingCount() {
      return pending;
    },
    get failedCount() {
      return failed;
    },
    get hasFailures() {
      return failed > 0;
    },
  };
}
